import {
  AUDIT_ACTION_GROUPS,
  AUDIT_ACTION_GROUP_KEYS,
  AUDIT_ENTITY_LABELS,
  type AuditActionGroup,
  type AuditEntryDTO,
  type AuditListResponse,
  isAuditChange,
} from '@hearth/shared';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, ScrollText, Search } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { RelativeTime } from '../../components/crm';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge, Button, Card, EmptyState, Input, Pagination, Select, Skeleton } from '../../components/ui';
import { useLookups } from '../../hooks/useMeta';
import { api } from '../../lib/api';

const ACTOR_LABELS: Record<AuditEntryDTO['actorType'], string> = {
  user: 'Person',
  system: 'System',
  automation: 'Automation',
  import: 'Import',
  integration: 'Integration',
};

/** Who changed what, when, and with which values. Read-only. */
export function AuditPage() {
  const { meta } = useLookups();
  const [filters, setFilters] = useState({ group: '', actorUserId: '', action: '', q: '' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [open, setOpen] = useState<number | null>(null);

  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  for (const [key, value] of Object.entries(filters)) if (value) params.set(key, value);
  const { data, isLoading } = useQuery({
    queryKey: ['audit', params.toString()],
    queryFn: ({ signal }) => api.get<AuditListResponse>(`/api/audit?${params}`, { signal }),
  });

  const setFilter = (patch: Partial<typeof filters>) => {
    setFilters({ ...filters, ...patch });
    setPage(1);
  };

  return (
    <Page width="wide">
      <PageHeader title="Audit log" description="Every change and every automated action, with the values before and after." />
      <Card className="mb-4">
        <div className="flex flex-wrap items-center gap-2 p-3 text-[13px]">
          <Select size="sm" aria-label="Area" value={filters.group} onChange={(e) => setFilter({ group: e.target.value })}>
            <option value="">All areas</option>
            {AUDIT_ACTION_GROUP_KEYS.map((group: AuditActionGroup) => (
              <option key={group} value={group}>
                {AUDIT_ACTION_GROUPS[group].label}
              </option>
            ))}
          </Select>
          <Select size="sm" aria-label="Actor" value={filters.actorUserId} onChange={(e) => setFilter({ actorUserId: e.target.value })}>
            <option value="">Anyone</option>
            {(meta?.users ?? []).map((user) => (
              <option key={user.id} value={user.id}>
                {user.name}
              </option>
            ))}
          </Select>
          <Input size="sm" className="w-44" aria-label="Action prefix" placeholder="Action, e.g. lead." value={filters.action} onChange={(e) => setFilter({ action: e.target.value.trim() })} />
          <Input
            size="sm"
            className="w-64"
            aria-label="Search"
            placeholder="Search summaries and reasons…"
            icon={Search}
            value={filters.q}
            onChange={(e) => setFilter({ q: e.target.value })}
          />
          {Object.values(filters).some(Boolean) && (
            <Button size="sm" variant="ghost" onClick={() => setFilters({ group: '', actorUserId: '', action: '', q: '' })}>
              Clear
            </Button>
          )}
        </div>
      </Card>
      <Card>
        {isLoading ? (
          <Skeleton className="m-4 h-64" />
        ) : (data?.items.length ?? 0) === 0 ? (
          <EmptyState icon={ScrollText} title="Nothing matches" description="Change the filters, or clear them to see the most recent entries." />
        ) : (
          <>
            <ul className="divide-y divide-slate-100">
              {data!.items.map((entry) => (
                <li key={entry.id} className="text-[13px]">
                  <button type="button" className="flex w-full flex-wrap items-center gap-x-2 gap-y-1 px-4 py-2 text-left hover:bg-slate-50" onClick={() => setOpen(open === entry.id ? null : entry.id)}>
                    <ChevronRight className={`size-3.5 shrink-0 text-slate-400 transition-transform ${open === entry.id ? 'rotate-90' : ''}`} />
                    <code className="text-xs text-slate-500">{entry.action}</code>
                    <span className="text-slate-600">{entry.actor?.name ?? ACTOR_LABELS[entry.actorType]}</span>
                    <span className="min-w-0 flex-1 truncate text-slate-800">{entry.summary ?? ''}</span>
                    {entry.lead && (
                      <Link to={`/leads/${entry.lead.id}`} className="text-ember-700 hover:underline" onClick={(e) => e.stopPropagation()}>
                        {entry.lead.name}
                        {entry.lead.deleted ? ' (merged)' : ''}
                      </Link>
                    )}
                    <Badge size="sm">{AUDIT_ENTITY_LABELS[entry.entityType] ?? entry.entityType}</Badge>
                    <RelativeTime value={entry.createdAt} className="text-xs text-slate-400" />
                  </button>
                  {open === entry.id && (
                    <div className="space-y-1 border-t border-slate-50 bg-slate-50/60 px-10 py-2 text-xs text-slate-600">
                      {entry.reason && <p>Reason: {entry.reason}</p>}
                      {entry.changes && Object.keys(entry.changes).length > 0 && (
                        <table className="w-full">
                          <tbody>
                            {Object.entries(entry.changes).map(([field, change]) => (
                              <tr key={field}>
                                <td className="w-40 py-0.5 align-top font-medium text-slate-500">{field}</td>
                                <td className="py-0.5 align-top">
                                  {isAuditChange(change) ? (
                                    <span>
                                      {formatValue(change.from)} <span className="text-slate-400">→</span> {formatValue(change.to)}
                                    </span>
                                  ) : (
                                    formatValue(change)
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                      <p className="text-slate-400">
                        {entry.entityType} {entry.entityId ?? ''} {entry.ip ? `· ${entry.ip}` : ''} {entry.requestId ? `· ${entry.requestId}` : ''}
                      </p>
                    </div>
                  )}
                </li>
              ))}
            </ul>
            <Pagination
              page={data!.page}
              pageSize={data!.pageSize}
              total={data!.total}
              onChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
              className="border-t border-slate-100 px-4 py-2"
            />
            {data!.totalCapped && <p className="px-4 pb-3 text-xs text-slate-400">Showing the {data!.total.toLocaleString()} most recent entries — narrow the filters for older ones.</p>}
          </>
        )}
      </Card>
    </Page>
  );
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
}
